import { useToastController } from "@tamagui/toast";
import { useQueryClient } from "@tanstack/react-query";
import { useContext, useMemo } from "react";

import {
  getGetClinicRequestQueryKey,
  getGetClinicStatusForPatientQueryKey,
  getGetStatusOfEncounterAssociatedWithCuraiVisitQueryKey,
  useCreateCuraiVisitFromSuperclinicalEncounterForTheAuthenticatedRegisteredUser as useCreateCuraiVisit,
  useGetClinicStatusForPatient,
  useResumeCuraiVisit,
} from "@/api/generated";
import {
  DefaultVisitMetadataVisitTypePreference,
  EncounterStartType,
  type UserProgressionStateModel,
  VisitType,
} from "@/api/generated/models";
import { useMergeStatus } from "@/api/queries";
import { invalidateConversationHistory } from "@/api/queries/conversation";
import { LayoutContext } from "@/components";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { AD_CONVERSION_EVENTS } from "@/config/adConversionEvents";
import WaitingRoomComponent from "@/conversation/sidebar/WaitingRoom";
import VideoPreview from "@/conversation/video/VideoPreview";
import { useAnalytics } from "@/hooks/analytics";
import { updateCuraiConversationId } from "@/hooks/useInfiniteMessages";
import { useSmsConsentNudge } from "@/hooks/useSmsConsentNudge";
import { useActiveProgressionStore } from "@/store/activeProgression";
import { getResetProgressionFromError } from "@/utils/progressionReset";
import {
  ENCOUNTER_TYPE_BY_VISIT_TYPE,
  VISIT_TYPE_BY_ENCOUNTER_TYPE,
} from "@/utils/visitType";

type UseCuraiVisitRedirectOptions = {
  conversationId: string;
  clinicRequestId?: string;
  originClinicRequestId?: string;
  userProgressionId?: string;
  resume?: boolean;
  onVisitStarted?: () => void;
  // Called when visit creation is refused (402 entitlement / 409 selection mismatch). The backend
  // has reset the progression back to selection and returns it, so the caller renders that step
  // instead of stranding the user on the loading screen.
  onVisitBlocked?: (progression: UserProgressionStateModel) => void;
  visitTypePreference?: DefaultVisitMetadataVisitTypePreference;
  avCallRequired?: boolean;
  paymentVisitType?: VisitType;
};

export const useCuraiVisitRedirect = ({
  conversationId,
  clinicRequestId,
  originClinicRequestId,
  userProgressionId,
  resume = false,
  onVisitStarted,
  onVisitBlocked,
  visitTypePreference,
  avCallRequired,
  paymentVisitType,
}: UseCuraiVisitRedirectOptions) => {
  const queryClient = useQueryClient();
  const { setModalContent } = useContext(LayoutContext);
  const { show: showToast } = useToastController();
  const { track } = useAnalytics();
  // Errors are handled in onError below (render the reset step or a generic toast), so suppress the
  // global error toast.
  const { mutate: createCuraiVisit, isPending: isCreateCuraiVisitPending } =
    useCreateCuraiVisit({ mutation: { meta: { suppressErrorToast: true } } });
  const { mutate: resumeCuraiVisit, isPending: isResumeCuraiVisitPending } =
    useResumeCuraiVisit();
  const { data: clinicStatus, isLoading: isClinicStatusLoading } =
    useGetClinicStatusForPatient();
  const { triggerNudge: triggerSmsConsentNudge } =
    useSmsConsentNudge(conversationId);

  // Block starting a visit while an account merge is in progress. Gated on the
  // account-merge flag so no /merge-status traffic is generated (and no behavior
  // changes) when the flow is switched off.
  const { value: isAccountMergeEnabled } = useFeatureFlag(
    "curai-classic-to-chai-account-merge",
    false,
  );
  const { data: mergeStatus, isLoading: isMergeStatusLoading } = useMergeStatus(
    {
      enabled: isAccountMergeEnabled,
    },
  );
  const isBlockedByMerge =
    isAccountMergeEnabled &&
    mergeStatus?.patient_facing_status === "in_progress";

  const encounterType = useMemo(() => {
    // A stored payment_visit_type is the paid modality — the source of truth for the encounter
    // type. Only the no-selection free path falls back to preference and clinic status.
    if (paymentVisitType) {
      const fromSelection = ENCOUNTER_TYPE_BY_VISIT_TYPE[paymentVisitType];
      if (fromSelection) return fromSelection;
    }
    if (
      visitTypePreference ===
      DefaultVisitMetadataVisitTypePreference.audiovisual
    ) {
      return EncounterStartType.AUDIOVISUAL;
    }
    if (visitTypePreference === DefaultVisitMetadataVisitTypePreference.chat) {
      return avCallRequired
        ? EncounterStartType.AUDIOVISUAL
        : EncounterStartType.TEXT_BASED;
    }
    return clinicStatus?.needs_video_visit
      ? EncounterStartType.AUDIOVISUAL
      : EncounterStartType.TEXT_BASED;
  }, [
    paymentVisitType,
    avCallRequired,
    visitTypePreference,
    clinicStatus?.needs_video_visit,
  ]);

  // Include merge-status loading so callers (esp. ClinicRedirect's auto-start
  // effect) wait for it before starting a visit -- otherwise, once clinic status
  // resolves first, isBlockedByMerge is still false (merge status unknown) and a
  // visit could auto-start for a mid-merge patient. When the flag is off the
  // (disabled) merge query never loads, so this stays false.
  const isLoading = isClinicStatusLoading || isMergeStatusLoading;
  const isPending = isCreateCuraiVisitPending || isResumeCuraiVisitPending;

  const handleVisitRedirect = (curaiConversationId: string) => {
    if (encounterType === EncounterStartType.AUDIOVISUAL) {
      setModalContent(() => (props: any) => (
        <VideoPreview curaiConversationId={curaiConversationId} {...props} />
      ));
    } else {
      setModalContent(() => (props: any) => (
        <WaitingRoomComponent
          encounterType={encounterType}
          curaiConversationId={curaiConversationId}
          {...props}
        />
      ));
    }
  };

  const startVisit = () => {
    if (!conversationId || isLoading || isPending || isBlockedByMerge) return;

    if (resume) {
      resumeCuraiVisit(
        { conversationId },
        {
          onSuccess: () => {
            track(`superclinical-__conversation-resumed`, {
              event_context: {
                conversation_id: conversationId,
                conversation_type: "curai",
              },
            });
            queryClient.invalidateQueries({
              queryKey:
                getGetStatusOfEncounterAssociatedWithCuraiVisitQueryKey(
                  conversationId,
                ),
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: ["conversation", "infinite", conversationId],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: getGetClinicStatusForPatientQueryKey(),
            });
            handleVisitRedirect(conversationId);
            onVisitStarted?.();
          },
        },
      );
      return;
    }

    if (!userProgressionId) {
      throw new Error("A user progression is required to create a visit");
    }

    createCuraiVisit(
      {
        data: {
          conversation_id: conversationId,
          encounter_type: encounterType,
          clinic_request_id: clinicRequestId,
          origin_clinic_request_id: originClinicRequestId,
          user_progression_id: userProgressionId,
        },
      },
      {
        onSuccess: ({ new_conversation_id }) => {
          // Note: While the event shares a name with AD_CONVERSION_EVENTS.conversationCreated, the ad conversion
          // only measures created AI conversations. Since this is not used for ad conversion, we keep it as a string.
          track(`superclinical-__conversation-created`, {
            event_context: {
              conversation_id: new_conversation_id,
              conversation_type: "curai",
            },
          });
          track(AD_CONVERSION_EVENTS.deprecatedClinicVisitStarted, {
            event_context: {
              conversation_id: new_conversation_id,
              conversation_type: "curai",
            },
          });
          if (clinicRequestId) {
            const {
              checkoutPaymentProperties,
              clearCheckoutPaymentProperties,
            } = useActiveProgressionStore.getState();
            track(AD_CONVERSION_EVENTS.visitStarted, {
              ...(checkoutPaymentProperties[clinicRequestId] ?? {}),
              conversation_id: new_conversation_id,
              clinic_request_id: clinicRequestId,
              encounter_type: encounterType,
              visit_type:
                paymentVisitType ?? VISIT_TYPE_BY_ENCOUNTER_TYPE[encounterType],
              $distinct_id: `visit_started:${clinicRequestId}`,
            });
            clearCheckoutPaymentProperties(clinicRequestId);
          }

          // Queue the SMS consent nudge for text-based (live chat).
          // Video visits keep their own trigger on leaving the call.
          if (encounterType === EncounterStartType.TEXT_BASED) {
            triggerSmsConsentNudge(conversationId, "live-chat");
          }

          updateCuraiConversationId(
            conversationId,
            new_conversation_id,
            queryClient,
          );
          queryClient.invalidateQueries({
            queryKey:
              getGetStatusOfEncounterAssociatedWithCuraiVisitQueryKey(
                new_conversation_id,
              ),
            exact: false,
          });
          invalidateConversationHistory(queryClient);
          queryClient.invalidateQueries({
            queryKey: getGetClinicStatusForPatientQueryKey(),
          });
          if (clinicRequestId) {
            // need to refresh this immediately, otherwise the offer card in the chat stream will not realize
            // the active ongoing visit is its own and present "update my doctor" button
            queryClient.invalidateQueries({
              queryKey: getGetClinicRequestQueryKey(
                conversationId,
                clinicRequestId,
              ),
            });
          }
          handleVisitRedirect(new_conversation_id);
          onVisitStarted?.();
        },
        onError: (error) => {
          const resetProgression = getResetProgressionFromError(error);
          if (resetProgression && onVisitBlocked) {
            onVisitBlocked(resetProgression);
            return;
          }
          showToast("We couldn't start your visit. Please try again.");
        },
      },
    );
  };

  return {
    startVisit,
    encounterType,
    isLoading,
    isPending,
    isBlockedByMerge,
  };
};
